import { useMemo } from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  Sorting,
  AdminUsersSortingColumnEnum,
  SortingOrder,
  Filtering,
  CreateUserRequest,
  Scope,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import { useDispatch } from '@tabeeb/store'
import { useApi } from '@tabeeb/modules/shared/utils/API'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'

import { AdminUserQueryKeys } from '../queries'

const useUsers = ({
  pageNumber = 1,
  pageSize = 50,
  search = '',
  sorting = {
    Column: AdminUsersSortingColumnEnum.Email,
    Order: SortingOrder.Asc,
  },
  filtering = {
    Name: null,
    Email: null,
    Role: null,
  },
  scope = Scope.Tenant,
}: {
  pageNumber?: number
  pageSize?: number
  search?: string
  sorting?: Sorting
  filtering?: Filtering
  scope?: Scope
} = {}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const { mainApi: api } = useApi()

  const queryKey = [AdminUserQueryKeys.Users, { pageNumber, pageSize, search, sorting, filtering, scope }]

  const usersQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.adminUsers.getAsync({
        Sorting: sorting,
        Filtering: filtering,
        PageNumber: pageNumber,
        PageSize: pageSize,
        Scope: scope,
      }),
  })

  const disableMutation = useMutation({
    mutationFn: (request: { userId: number; subdomain: string }) =>
      api.adminUsers.updateDisableUserByIdAsync(request.userId, {
        headers: { 'X-Tenant-Subdomain': request.subdomain },
      }),
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to disable user' }))
    },
    onSuccess: (_, { userId }) => {
      queryClient.setQueryData<typeof usersQuery.data>(queryKey, (prev) => {
        if (!prev) {
          return prev
        }

        return prev.map((user) => (user.Id === userId ? { ...user, IsDisabled: true } : user))
      })
    },
  })

  const enableMutation = useMutation({
    mutationFn: (request: { userId: number; subdomain: string }) =>
      api.adminUsers.updateEnableUserByIdAsync(request.userId, {
        headers: { 'X-Tenant-Subdomain': request.subdomain },
      }),
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to enable user' }))
    },
    onSuccess: (_, { userId }) => {
      queryClient.setQueryData<typeof usersQuery.data>(queryKey, (prev) => {
        if (!prev) {
          return prev
        }

        return prev.map((user) => (user.Id === userId ? { ...user, IsDisabled: false } : user))
      })
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: (request: { userId: number; subdomain: string }) =>
      api.adminUsers.resetUserPassword(request.userId, {
        headers: { 'X-Tenant-Subdomain': request.subdomain },
      }),
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to reset user password' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Password reset requested' }))
      usersQuery.refetch()
    },
  })

  const updateUserMutation = useMutation({
    mutationFn: async (
      request: CreateUserRequest & { Id: number; UserName: string; IsCreatedByAdmin: boolean; Subdomain: string }
    ) => {
      const userResponse = await api.adminUsers.putAsync(request.Id, request, {
        headers: { 'X-Tenant-Subdomain': request.Subdomain },
      })
      await api.userRoles.assignRoleAsync(
        { UserId: request.Id, TenantId: request.TenantId!, Role: request.Role },
        {
          headers: { 'X-Tenant-Subdomain': request.Subdomain },
        }
      )

      return userResponse
    },
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to update user' }))
    },
    onSuccess: (response, request) => {
      queryClient.setQueryData<typeof usersQuery.data>(queryKey, (prev) => {
        if (!prev) {
          return prev
        }

        return prev.map((user) =>
          user.Id === request.Id
            ? {
                ...user,
                Role: request.Role,
                Email: response.Email,
                FirstName: response.FirstName,
                LastName: response.LastName,
                EmployeeId: response.EmployeeId,
                Name: response.Name,
              }
            : user
        )
      })
    },
  })

  const createUserMutation = useMutation({
    mutationFn: api.adminUsers.postCreateAsync,
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to create user' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'User was successfully created' }))
      usersQuery.refetch()
    },
  })

  const createUsersMutation = useMutation({
    mutationFn: api.adminUsers.postCreateUsersAsync,
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to upload users' }))
    },
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Users were successfully uploaded' }))
      usersQuery.refetch()
    },
  })

  return useMemo(
    () => ({
      loading: usersQuery.isLoading,
      loaded: usersQuery.isFetched,
      total: (usersQuery.data ?? [])[0]?.TotalCount ?? 0,
      users: usersQuery.data ?? [],
      onReload: () => usersQuery.refetch(),
      onDisableUserAsync: disableMutation.mutateAsync,
      onEnableUserAsync: enableMutation.mutateAsync,
      onResetPassword: resetPasswordMutation.mutate,
      onUpdateUserAsync: updateUserMutation.mutateAsync,
      onCreateUser: createUserMutation.mutate,
      onCreateUsers: createUsersMutation.mutate,
    }),
    [
      usersQuery,
      createUserMutation.mutate,
      createUsersMutation.mutate,
      disableMutation.mutateAsync,
      enableMutation.mutateAsync,
      resetPasswordMutation.mutate,
      updateUserMutation.mutateAsync,
    ]
  )
}

export default useUsers
